import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Search } from "lucide-react";

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const category = searchParams.get("category") || "";
  const q = searchParams.get("q") || "";
  const sort = searchParams.get("sort") || "newest";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (q) params.set("q", q);
    if (sort) params.set("sort", sort);
    api
      .get(`/products?${params.toString()}`)
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category, q, sort]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  return (
    <div data-testid="browse-page" className="max-w-7xl mx-auto px-4 md:px-8 py-12">
      <div className="mb-8">
        <span className="tag tag-accent">// MARKETPLACE</span>
        <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white mt-3">
          {category ? categories.find((c) => c.id === category)?.name || "Browse" : "All Products"}
        </h1>
        <p className="text-white/55 mt-2">
          {q ? `Searching for "${q}" · ` : ""}{products.length} items found
        </p>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="space-y-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              data-testid="browse-search-input"
              defaultValue={q}
              onKeyDown={(e) => {
                if (e.key === "Enter") updateParam("q", e.currentTarget.value);
              }}
              placeholder="Search..."
              className="w-full bg-black border border-white/15 focus:border-[#00F0FF] focus:outline-none text-white text-sm pl-10 pr-4 py-2.5"
            />
          </div>
          <div>
            <h3 className="text-xs uppercase font-bold tracking-[0.2em] text-white/60 mb-3">Game</h3>
            <ul className="space-y-1">
              <li>
                <button
                  data-testid="filter-all"
                  onClick={() => updateParam("category", "")}
                  className={`text-sm w-full text-left py-1.5 px-2 ${!category ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-white/70 hover:text-white"}`}
                >
                  All Games
                </button>
              </li>
              {categories.map((c) => (
                <li key={c.id}>
                  <button
                    data-testid={`filter-${c.id}`}
                    onClick={() => updateParam("category", c.id)}
                    className={`text-sm w-full text-left py-1.5 px-2 ${category === c.id ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-white/70 hover:text-white"}`}
                  >
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-xs uppercase font-bold tracking-[0.2em] text-white/60 mb-3">Sort by</h3>
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => updateParam("sort", e.target.value)}
              className="w-full bg-black border border-white/15 focus:border-[#00F0FF] text-white text-sm px-3 py-2.5"
            >
              <option value="newest">Newest</option>
              <option value="popular">Most popular</option>
              <option value="price_asc">Price: low → high</option>
              <option value="price_desc">Price: high → low</option>
            </select>
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="text-white/50 text-center py-20">Loading...</div>
          ) : products.length === 0 ? (
            <div className="text-white/50 text-center py-20 border border-white/10" data-testid="no-products">
              No products found. Try a different filter.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
